/* eslint-disable no-console */

import type { TGender, THairColor, TSkinColor } from '@shared/common/product';
import {
  INPUT_VALUE_ENGLISH,
  INPUT_VALUE_JAPANESE,
} from '@shared/common/product';
import type { IBookTMLetsMakeMetroData } from '@shared/jp/models';
import type {
  THairStyleBoyLMM,
  THairStyleGirlLMM,
  TKunChan,
  TLanguage,
  TStoryLinesLMM,
} from '@shared/jp/product';
import { KUNCHAN_VALUE_TO_LABEL_MAP, languageArr } from '@shared/jp/product';
import {
  resolveAssetElement,
  segmentsListToFontsMap,
} from '@studiobuki/web-core/lib/book-common';
import type {
  TermsWelcomeToTheWold,
  PageTermsWelcomeToTheWold,
  Segment,
  ImageElement,
  TextElement,
  AssetElement,
  Page,
  TextStyle,
} from '@studiobuki/web-core/lib/book-common';
import { isNode } from 'browser-or-node';
import { jp } from './locale';
import { KEY_KIDS_HERONAME, KEY_KIDS_TITLE } from './_constants';
import { APJapanesefont_000000_18 } from './locale/jp/_styles';

type TSegmentTermsType = TermsWelcomeToTheWold;
type TPageTermsType = PageTermsWelcomeToTheWold;

type TSegment = Segment<
  TSegmentTermsType,
  TPageTermsType,
  IBookTMLetsMakeMetroData
>;

type TLeftTextElementStyle = Partial<TextStyle> &
  Required<Pick<TextStyle, 'fontSize' | 'fontFamily' | 'fill'>>;
const leftTextElement = (
  bookData: IBookTMLetsMakeMetroData,
  _text: string,
  baseY: number,
  _lgStyle: TLeftTextElementStyle,
  _smStyle: TLeftTextElementStyle,
  lgWidth: number,
  smWidth: number,
  xToAdd: number,
) => {
  const textArr: TextElement[] = [];
  const text = textProcessor(bookData, _text);

  const baselineK = 0.2;

  const baseStyle = {
    letterSpacing: 1,
    lineHeight: 1.9,
  } as const;

  const lgStyle: TextStyle = {
    ...baseStyle,
    ..._lgStyle,
  };

  // @ts-ignore
  const lgHeight = lgStyle.fontSize * lgStyle.lineHeight;
  const smStyle: TextStyle = {
    ...baseStyle,
    ..._smStyle,
  };

  // @ts-ignore
  const smHeight = smStyle.fontSize * smStyle.lineHeight;
  textArr.push(
    ...text.split('').reduce<TextElement[]>((acc, symbol, i, arr) => {
      if (['[', ']'].includes(symbol)) return acc;

      const prevSymbol = arr[i - 1];
      const prevText = acc[acc.length - 1];
      const isPrevBig = prevText?.style === lgStyle;
      const isBig =
        prevSymbol === ']' ? false : prevSymbol === '[' ? true : isPrevBig;

      acc.push({
        text: symbol,
        style: isBig ? lgStyle : smStyle,
        // @ts-ignore
        x: prevText ? prevText.x + (isPrevBig ? lgWidth : smWidth) : 0,
        y: isBig
          ? baseY
          : baseY +
            (lgHeight - lgHeight * baselineK) -
            // (smHeight - smHeight * baselineK),
            (smHeight + smHeight * baselineK),
      });

      return acc;
    }, []),
  );

  // * center text

  // const xToAdd = 125;

  textArr.forEach((_text) => {
    // @ts-ignore
    _text.x += xToAdd;
  });

  return textArr;
};

type TCenterTextElementStyle = Partial<TextStyle> &
  Required<Pick<TextStyle, 'fontSize' | 'fontFamily' | 'fill'>>;
const centerTextElement = (
  bookData: IBookTMLetsMakeMetroData,
  _text: string,
  baseY: number,
  _lgStyle: TCenterTextElementStyle,
  _smStyle: TCenterTextElementStyle,
  lgWidth: number,
  smWidth: number,
) => {
  const textArr: TextElement[] = [];
  const text = textProcessor(bookData, _text);
  // const baseWidth =
  //   bookData.cover === 'hard' ? (isNode ? 842 : 842) : isNode ? 842 : 842;
  const baseWidth = 205;
  const baselineK = 0.15;

  const baseStyle = {
    letterSpacing: 1,
    lineHeight: 1.4,
  } as const;

  const lgStyle: TextStyle = {
    ...baseStyle,
    ..._lgStyle,
  };

  // @ts-ignore
  const lgHeight = lgStyle.fontSize * lgStyle.lineHeight;
  const smStyle: TextStyle = {
    ...baseStyle,
    ..._smStyle,
  };

  // @ts-ignore
  const smHeight = smStyle.fontSize * smStyle.lineHeight;
  textArr.push(
    ...text.split('').reduce<TextElement[]>((acc, symbol, i, arr) => {
      if (['[', ']'].includes(symbol)) return acc;

      const prevSymbol = arr[i - 1];
      const prevText = acc[acc.length - 1];
      const isPrevBig = prevText?.style === lgStyle;
      const isBig =
        prevSymbol === ']' ? false : prevSymbol === '[' ? true : isPrevBig;

      acc.push({
        text: symbol,
        style: isBig ? lgStyle : smStyle,
        // @ts-ignore
        x: prevText ? prevText.x + (isPrevBig ? lgWidth : smWidth) : 0,
        y: isBig
          ? baseY
          : baseY +
            (lgHeight - lgHeight * baselineK) -
            // (smHeight - smHeight * baselineK),
            (smHeight + smHeight * baselineK),
      });

      return acc;
    }, []),
  );

  // * center text
  const fullWidth = textArr.reduce(
    (width, _text) => width + (_text.style === lgStyle ? lgWidth : smWidth),
    0,
  );
  const xToAdd = baseWidth / 2 - fullWidth / 2;

  textArr.forEach((_text) => {
    // @ts-ignore
    _text.x += xToAdd;
  });

  return textArr;
};

export const textProcessor = (
  bookData: IBookTMLetsMakeMetroData,
  _textStr: string,
): string => {
  //
  let textStr = _textStr;

  textStr = textStr.replace(
    new RegExp(`${KEY_KIDS_TITLE}`, 'gi'),
    bookData.kunChan === 'nashi'
      ? ''
      : KUNCHAN_VALUE_TO_LABEL_MAP[bookData.kunChan],
  );
  textStr = textStr.replace(
    new RegExp(`${KEY_KIDS_HERONAME}`, 'gi'),
    bookData.heroName,
  );
  return textStr;
};
const skinColorConfig: {
  [gender in TGender]: {
    [skinColor in TSkinColor]:
      | ImageElement
      | AssetElement<{ assetSkin: string }>;
  };
} = {
  boy: {
    white: {
      type: 'AssetElement',
      asset: {
        assetSkin: '01',
      },
    },
    nutmeg: {
      type: 'AssetElement',
      asset: {
        assetSkin: '02',
      },
    },
    brown: {
      type: 'AssetElement',
      asset: {
        assetSkin: '00',
      },
    },
    black: {
      type: 'AssetElement',
      asset: {
        assetSkin: '00',
      },
    },
  },

  girl: {
    white: {
      type: 'AssetElement',
      asset: {
        assetSkin: '03',
      },
    },
    nutmeg: {
      type: 'AssetElement',
      asset: {
        assetSkin: '04',
      },
    },
    brown: {
      type: 'AssetElement',
      asset: {
        assetSkin: '00',
      },
    },
    black: {
      type: 'AssetElement',
      asset: {
        assetSkin: '00',
      },
    },
  },
};

const hairColorConfig: {
  [gender in TGender]: {
    [hairColor in THairColor]: {
      [hairStyle in THairStyleBoyLMM | THairStyleGirlLMM]:
        | ImageElement
        | AssetElement<{ assetHair: string }>;
    };
  };
} = {
  boy: {
    blonde: {
      buzz: { type: 'AssetElement', asset: { assetHair: '01' } },
      very_short: { type: 'AssetElement', asset: { assetHair: '02' } },
      short: { type: 'AssetElement', asset: { assetHair: '03' } },
      mushroom: { type: 'AssetElement', asset: { assetHair: '04' } },
      long: { type: 'AssetElement', asset: { assetHair: '05' } },
      curly: { type: 'AssetElement', asset: { assetHair: '06' } },
      short_bob: { type: 'AssetElement', asset: { assetHair: '00' } },
      medium_bob: { type: 'AssetElement', asset: { assetHair: '00' } },
      long_with_bang: { type: 'AssetElement', asset: { assetHair: '00' } },
      pig_tail: { type: 'AssetElement', asset: { assetHair: '00' } },
      long_curl: { type: 'AssetElement', asset: { assetHair: '00' } },
    },
    red: {
      buzz: { type: 'AssetElement', asset: { assetHair: '07' } },
      very_short: { type: 'AssetElement', asset: { assetHair: '08' } },
      short: { type: 'AssetElement', asset: { assetHair: '09' } },
      mushroom: { type: 'AssetElement', asset: { assetHair: '10' } },
      long: { type: 'AssetElement', asset: { assetHair: '11' } },
      curly: { type: 'AssetElement', asset: { assetHair: '12' } },
      short_bob: { type: 'AssetElement', asset: { assetHair: '00' } },
      medium_bob: { type: 'AssetElement', asset: { assetHair: '00' } },
      long_with_bang: { type: 'AssetElement', asset: { assetHair: '00' } },
      pig_tail: { type: 'AssetElement', asset: { assetHair: '00' } },
      long_curl: { type: 'AssetElement', asset: { assetHair: '00' } },
    },
    brown: {
      buzz: { type: 'AssetElement', asset: { assetHair: '13' } },
      very_short: { type: 'AssetElement', asset: { assetHair: '14' } },
      short: { type: 'AssetElement', asset: { assetHair: '15' } },
      mushroom: { type: 'AssetElement', asset: { assetHair: '16' } },
      long: { type: 'AssetElement', asset: { assetHair: '17' } },
      curly: { type: 'AssetElement', asset: { assetHair: '18' } },
      short_bob: { type: 'AssetElement', asset: { assetHair: '00' } },
      medium_bob: { type: 'AssetElement', asset: { assetHair: '00' } },
      long_with_bang: { type: 'AssetElement', asset: { assetHair: '00' } },
      pig_tail: { type: 'AssetElement', asset: { assetHair: '00' } },
      long_curl: { type: 'AssetElement', asset: { assetHair: '00' } },
    },
    black: {
      buzz: { type: 'AssetElement', asset: { assetHair: '19' } },
      very_short: { type: 'AssetElement', asset: { assetHair: '20' } },
      short: { type: 'AssetElement', asset: { assetHair: '21' } },
      mushroom: { type: 'AssetElement', asset: { assetHair: '22' } },
      long: { type: 'AssetElement', asset: { assetHair: '23' } },
      curly: { type: 'AssetElement', asset: { assetHair: '24' } },
      short_bob: { type: 'AssetElement', asset: { assetHair: '00' } },
      medium_bob: { type: 'AssetElement', asset: { assetHair: '00' } },
      long_with_bang: { type: 'AssetElement', asset: { assetHair: '00' } },
      pig_tail: { type: 'AssetElement', asset: { assetHair: '00' } },
      long_curl: { type: 'AssetElement', asset: { assetHair: '00' } },
    },
  },
  girl: {
    blonde: {
      short: { type: 'AssetElement', asset: { assetHair: '25' } },
      short_bob: { type: 'AssetElement', asset: { assetHair: '26' } },
      medium_bob: { type: 'AssetElement', asset: { assetHair: '27' } },
      long_with_bang: { type: 'AssetElement', asset: { assetHair: '28' } },
      pig_tail: { type: 'AssetElement', asset: { assetHair: '29' } },
      long_curl: { type: 'AssetElement', asset: { assetHair: '30' } },
      buzz: { type: 'AssetElement', asset: { assetHair: '00' } },
      very_short: { type: 'AssetElement', asset: { assetHair: '00' } },
      mushroom: { type: 'AssetElement', asset: { assetHair: '00' } },
      long: { type: 'AssetElement', asset: { assetHair: '00' } },
      curly: { type: 'AssetElement', asset: { assetHair: '00' } },
    },
    red: {
      short: { type: 'AssetElement', asset: { assetHair: '31' } },
      short_bob: { type: 'AssetElement', asset: { assetHair: '32' } },
      medium_bob: { type: 'AssetElement', asset: { assetHair: '33' } },
      long_with_bang: { type: 'AssetElement', asset: { assetHair: '34' } },
      pig_tail: { type: 'AssetElement', asset: { assetHair: '35' } },
      long_curl: { type: 'AssetElement', asset: { assetHair: '36' } },
      buzz: { type: 'AssetElement', asset: { assetHair: '00' } },
      very_short: { type: 'AssetElement', asset: { assetHair: '00' } },
      mushroom: { type: 'AssetElement', asset: { assetHair: '00' } },
      long: { type: 'AssetElement', asset: { assetHair: '00' } },
      curly: { type: 'AssetElement', asset: { assetHair: '00' } },
    },
    brown: {
      short: { type: 'AssetElement', asset: { assetHair: '37' } },
      short_bob: { type: 'AssetElement', asset: { assetHair: '38' } },
      medium_bob: { type: 'AssetElement', asset: { assetHair: '39' } },
      long_with_bang: { type: 'AssetElement', asset: { assetHair: '40' } },
      pig_tail: { type: 'AssetElement', asset: { assetHair: '41' } },
      long_curl: { type: 'AssetElement', asset: { assetHair: '42' } },
      buzz: { type: 'AssetElement', asset: { assetHair: '00' } },
      very_short: { type: 'AssetElement', asset: { assetHair: '00' } },
      mushroom: { type: 'AssetElement', asset: { assetHair: '00' } },
      long: { type: 'AssetElement', asset: { assetHair: '00' } },
      curly: { type: 'AssetElement', asset: { assetHair: '00' } },
    },
    black: {
      short: { type: 'AssetElement', asset: { assetHair: '43' } },
      short_bob: { type: 'AssetElement', asset: { assetHair: '44' } },
      medium_bob: { type: 'AssetElement', asset: { assetHair: '45' } },
      long_with_bang: { type: 'AssetElement', asset: { assetHair: '46' } },
      pig_tail: { type: 'AssetElement', asset: { assetHair: '47' } },
      long_curl: { type: 'AssetElement', asset: { assetHair: '48' } },
      buzz: { type: 'AssetElement', asset: { assetHair: '00' } },
      very_short: { type: 'AssetElement', asset: { assetHair: '00' } },
      mushroom: { type: 'AssetElement', asset: { assetHair: '00' } },
      long: { type: 'AssetElement', asset: { assetHair: '00' } },
      curly: { type: 'AssetElement', asset: { assetHair: '00' } },
    },
  },
};

const segment1Pages: {
  [key in TKunChan]: Page<TPageTermsType>[];
} = {
  kun: [
    {
      pageId: '1',
      pageImageURL: 'assets/templates_lets-make-metro/01005/01005.png',
      pageImagePDFURL: 'assets/templates_lets-make-metro/01005/01005.pdf',
      photoPosition: {
        x: 220,
        y: 172,
        width: 271,
        height: 271,
        rotation: -9,
      },
      text: [
        jp.SEGMENT_1_text_1,
        jp.SEGMENT_1_text_2_kun,
        jp.SEGMENT_1_text_3_kun,
      ],
    },
  ],
  chan: [
    {
      pageId: '1',
      pageImageURL: 'assets/templates_lets-make-metro/01005/01005.png',
      pageImagePDFURL: 'assets/templates_lets-make-metro/01005/01005.pdf',
      photoPosition: {
        x: 220,
        y: 172,
        width: 271,
        height: 271,
        rotation: -9,
      },
      text: [
        jp.SEGMENT_1_text_1,
        jp.SEGMENT_1_text_2_chan,
        jp.SEGMENT_1_text_3_chan,
      ],
    },
  ],
  san: [
    {
      pageId: '1',
      pageImageURL: 'assets/templates_lets-make-metro/01005/01005.png',
      pageImagePDFURL: 'assets/templates_lets-make-metro/01005/01005.pdf',
      photoPosition: {
        x: 220,
        y: 172,
        width: 271,
        height: 271,
        rotation: -9,
      },
      text: [
        jp.SEGMENT_1_text_1,
        jp.SEGMENT_1_text_2_san,
        jp.SEGMENT_1_text_3_san,
      ],
    },
  ],
  nashi: [
    {
      pageId: '1',
      pageImageURL: 'assets/templates_lets-make-metro/01005/01005.png',
      pageImagePDFURL: 'assets/templates_lets-make-metro/01005/01005.pdf',
      photoPosition: {
        x: 220,
        y: 172,
        width: 271,
        height: 271,
        rotation: -9,
      },
      text: [jp.SEGMENT_1_text_1, jp.SEGMENT_1_text_2_nashi],
    },
  ],
};
const segment13Pages: {
  [key in TStoryLinesLMM]: Page<TPageTermsType>[];
} = {
  train_creation: [
    {
      pageId: '13063',
      pageImageURL: 'assets/templates_lets-make-metro/13063/13063.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/13063/13063.pdf',

      text: [jp.SEGMENT_13_text_1_A],
    },
  ],
  tunnel_creation: [
    {
      pageId: '13064',
      pageImageURL: 'assets/templates_lets-make-metro/13064/13064.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/13064/13064.pdf',

      text: [jp.SEGMENT_13_text_1_B],
    },
  ],
  station_creation: [
    {
      pageId: '13065',
      pageImageURL: 'assets/templates_lets-make-metro/13065/13065.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/13065/13065.pdf',

      text: [jp.SEGMENT_13_text_1_C],
    },
  ],
  train_conductor: [
    {
      pageId: '13066',
      pageImageURL: 'assets/templates_lets-make-metro/13066/13066.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/13066/13066.pdf',

      text: [jp.SEGMENT_13_text_1_D],
    },
  ],
};

const segment14Pages: {
  [key in TStoryLinesLMM]: Page<TPageTermsType>[];
} = {
  train_creation: [
    {
      pageId: '99031',
      pageImageURL: 'assets/templates_lets-make-metro/99031/99031.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99031/99031.pdf',
    },
  ],
  tunnel_creation: [
    {
      pageId: '99032',
      pageImageURL: 'assets/templates_lets-make-metro/99032/99032.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99032/99032.pdf',
      text: [jp.SEGMENT_99032_text_1],
    },
  ],
  station_creation: [
    {
      pageId: '99033',
      pageImageURL: 'assets/templates_lets-make-metro/99033/99033.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99033/99033.pdf',
      text: [jp.SEGMENT_99033_text_1],
    },
  ],
  train_conductor: [
    {
      pageId: '99034',
      pageImageURL: 'assets/templates_lets-make-metro/99034/99034.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99034/99034.pdf',
      text: [jp.SEGMENT_99034_text_1],
    },
  ],
};
const segment15Pages: {
  [key in TStoryLinesLMM]: Page<TPageTermsType>[];
} = {
  train_creation: [
    {
      pageId: '99035',
      pageImageURL: 'assets/templates_lets-make-metro/99035/99035.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99035/99035.pdf',
      text: [jp.SEGMENT_99035_text_1],
    },
  ],
  tunnel_creation: [
    {
      pageId: '99036',
      pageImageURL: 'assets/templates_lets-make-metro/99036/99036.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99036/99036.pdf',
    },
  ],
  station_creation: [
    {
      pageId: '99037',
      pageImageURL: 'assets/templates_lets-make-metro/99037/99037.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99037/99037.pdf',
      text: [jp.SEGMENT_99037_text_1],
    },
  ],
  train_conductor: [
    {
      pageId: '99038',
      pageImageURL: 'assets/templates_lets-make-metro/99038/99038.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99038/99038.pdf',
      text: [jp.SEGMENT_99038_text_1],
    },
  ],
};
const segment16Pages: {
  [key in TStoryLinesLMM]: Page<TPageTermsType>[];
} = {
  train_creation: [
    {
      pageId: '99039',
      pageImageURL: 'assets/templates_lets-make-metro/99039/99039.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99039/99039.pdf',
      text: [jp.SEGMENT_99039_text_1, jp.SEGMENT_99039_text_2],
    },
  ],
  tunnel_creation: [
    {
      pageId: '99040',
      pageImageURL: 'assets/templates_lets-make-metro/99040/99040.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99040/99040.pdf',
      text: [jp.SEGMENT_99040_text_1],
    },
  ],
  station_creation: [
    {
      pageId: '99041',
      pageImageURL: 'assets/templates_lets-make-metro/99041/99041.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99041/99041.pdf',
      text: [
        jp.SEGMENT_99041_text_1,
        jp.SEGMENT_99041_text_2,
        jp.SEGMENT_99041_text_1,
      ],
    },
  ],
  train_conductor: [
    {
      pageId: '99042',
      pageImageURL: 'assets/templates_lets-make-metro/99042/99042.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99042/99042.pdf',
    },
  ],
};
const segment17Pages: {
  [key in TStoryLinesLMM]: Page<TPageTermsType>[];
} = {
  train_creation: [
    {
      pageId: '99043',
      pageImageURL: 'assets/templates_lets-make-metro/99043/99043.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99043/99043.pdf',
      text: [jp.SEGMENT_99043_text_1, jp.SEGMENT_99043_text_2],
    },
  ],
  tunnel_creation: [
    {
      pageId: '99044',
      pageImageURL: 'assets/templates_lets-make-metro/99044/99044.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99044/99044.pdf',
      text: [jp.SEGMENT_99044_text_1, jp.SEGMENT_99044_text_2],
    },
  ],
  station_creation: [
    {
      pageId: '99045',
      pageImageURL: 'assets/templates_lets-make-metro/99045/99045.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99045/99045.pdf',
      text: [jp.SEGMENT_99045_text_1],
    },
  ],
  train_conductor: [
    {
      pageId: '99046',
      pageImageURL: 'assets/templates_lets-make-metro/99046/99046.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99046/99046.pdf',
    },
  ],
};
const segment18Pages: {
  [key in TStoryLinesLMM]: Page<TPageTermsType>[];
} = {
  train_creation: [
    {
      pageId: '99047',
      pageImageURL: 'assets/templates_lets-make-metro/99047/99047.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99047/99047.pdf',
      text: [jp.SEGMENT_99047_text_1, jp.SEGMENT_99047_text_2],
    },
  ],
  tunnel_creation: [
    {
      pageId: '99048',
      pageImageURL: 'assets/templates_lets-make-metro/99048/99048.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99048/99048.pdf',
      text: [jp.SEGMENT_99048_text_1],
    },
  ],
  station_creation: [
    {
      pageId: '99049',
      pageImageURL: 'assets/templates_lets-make-metro/99049/99049.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99049/99049.pdf',
      text: [jp.SEGMENT_99049_text_1, jp.SEGMENT_99049_text_2],
    },
  ],
  train_conductor: [
    {
      pageId: '99050',
      pageImageURL: 'assets/templates_lets-make-metro/99050/99050.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99050/99050.pdf',
    },
  ],
};
const segment19Pages: {
  [key in TStoryLinesLMM]: Page<TPageTermsType>[];
} = {
  train_creation: [
    {
      pageId: '99051',
      pageImageURL: 'assets/templates_lets-make-metro/99051/99051.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99051/99051.pdf',
    },
  ],
  tunnel_creation: [
    {
      pageId: '99052',
      pageImageURL: 'assets/templates_lets-make-metro/99052/99052.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99052/99052.pdf',
    },
  ],
  station_creation: [
    {
      pageId: '99053',
      pageImageURL: 'assets/templates_lets-make-metro/99053/99053.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99053/99053.pdf',
      text: [jp.SEGMENT_99053_text_1],
    },
  ],
  train_conductor: [
    {
      pageId: '99054',
      pageImageURL: 'assets/templates_lets-make-metro/99054/99054.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99054/99054.pdf',
      text: [jp.SEGMENT_99054_text_1],
    },
  ],
};
const segment20Pages: {
  [key in TStoryLinesLMM]: Page<TPageTermsType>[];
} = {
  train_creation: [
    {
      pageId: '99055',
      pageImageURL: 'assets/templates_lets-make-metro/99055/99055.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99055/99055.pdf',
    },
  ],
  tunnel_creation: [
    {
      pageId: '99056',
      pageImageURL: 'assets/templates_lets-make-metro/99056/99056.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99056/99056.pdf',
      text: [jp.SEGMENT_99056_text_1],
    },
  ],
  station_creation: [
    {
      pageId: '99057',
      pageImageURL: 'assets/templates_lets-make-metro/99057/99057.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99057/99057.pdf',
    },
  ],
  train_conductor: [
    {
      pageId: '99058',
      pageImageURL: 'assets/templates_lets-make-metro/99058/99058.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99058/99058.pdf',
    },
  ],
};
const segment21Pages: {
  [key in TStoryLinesLMM]: Page<TPageTermsType>[];
} = {
  train_creation: [
    {
      pageId: '99059',
      pageImageURL: 'assets/templates_lets-make-metro/99059/99059.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99059/99059.pdf',
      text: [jp.SEGMENT_99059_text_1],
    },
  ],
  tunnel_creation: [
    {
      pageId: '99060',
      pageImageURL: 'assets/templates_lets-make-metro/99060/99060.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99060/99060.pdf',
      text: [jp.SEGMENT_99060_text_1],
    },
  ],
  station_creation: [
    {
      pageId: '99061',
      pageImageURL: 'assets/templates_lets-make-metro/99061/99061.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99061/99061.pdf',
      text: [jp.SEGMENT_99061_text_1],
    },
  ],
  train_conductor: [
    {
      pageId: '99062',
      pageImageURL: 'assets/templates_lets-make-metro/99062/99062.jpg',
      pageImagePDFURL: 'assets/templates_lets-make-metro/99062/99062.pdf',
      text: [jp.SEGMENT_99062_text_1],
    },
  ],
};

const segments = [
  {
    segmentId: '0',
    textProcessor: (bookData, text) => textProcessor(bookData, text),
    objectProcessor: (bookData: IBookTMLetsMakeMetroData) => {
      const objects: (ImageElement | TextElement)[] = [];
      const patternSkin: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL:
          'assets/templates_lets-make-metro/00002/M/00002-M{{assetSkin}}.png',
        imagePDFURL:
          'assets/templates_lets-make-metro/00002/M/00002-M{{assetSkin}}.pdf',
        depth: 5,
      };
      const patternHair: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL:
          'assets/templates_lets-make-metro/00002/H/00002-H{{assetHair}}.png',
        imagePDFURL:
          'assets/templates_lets-make-metro/00002/H/00002-H{{assetHair}}.pdf',
        depth: 7,
      };

      const patternGlasses: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL: 'assets/templates_lets-make-metro/00002/G/00002-G01.png',
        imagePDFURL: 'assets/templates_lets-make-metro/00002/G/00002-G01.pdf',
        depth: 6,
      };

      objects.push(
        ...[
          resolveAssetElement(
            skinColorConfig[bookData.gender][bookData.skinColor],
            patternSkin,
          ),
          resolveAssetElement(
            hairColorConfig[bookData.gender][bookData.hairColor][
              bookData.hairStyle
            ],
            patternHair,
          ),
        ],
      );

      if (bookData.glasses) {
        objects.push(...[...objects, patternGlasses]);
      }

      return objects;
    },

    pages: [
      {
        pageId: '0',
        pageImageURL: 'assets/templates_lets-make-metro/00002/00002.jpg',
        pageImagePDFURL: 'assets/templates_lets-make-metro/00002/00002.pdf',
        text: [jp.SEGMENT_0_text_1, jp.SEGMENT_0_text_2],
      },
    ],
  } as TSegment,
  {
    segmentId: '1',
    textProcessor: (bookData, text) => textProcessor(bookData, text),
    getPages: (bookData: IBookTMLetsMakeMetroData) =>
      segment1Pages[bookData.kunChan],
    pages: [],
  } as TSegment,
  {
    segmentId: '2',
    pages: [
      {
        pageId: '2',
        pageImageURL: 'assets/templates_lets-make-metro/02006/02006.jpg',
        pageImagePDFURL: 'assets/templates_lets-make-metro/02006/02006.pdf',
      },
    ],
  } as TSegment,
  {
    segmentId: '3',
    textProcessor: (bookData, text) => textProcessor(bookData, text),
    objectProcessor: (bookData) => {
      const objects: (ImageElement | TextElement)[] = [];
      const patternSkin: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL:
          'assets/templates_lets-make-metro/03007/M/03007-M{{assetSkin}}.png',
        imagePDFURL:
          'assets/templates_lets-make-metro/03007/M/03007-M{{assetSkin}}.pdf',
        depth: 5,
      };
      const patternHair: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL:
          'assets/templates_lets-make-metro/03007/H/03007-H{{assetHair}}.png',
        imagePDFURL:
          'assets/templates_lets-make-metro/03007/H/03007-H{{assetHair}}.pdf',
        depth: 7,
      };

      const patternGlasses: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL: 'assets/templates_lets-make-metro/03007/G/03007-G01.png',
        imagePDFURL: 'assets/templates_lets-make-metro/03007/G/03007-G01.pdf',
        depth: 6,
      };

      objects.push(
        ...[
          resolveAssetElement(
            skinColorConfig[bookData.gender][bookData.skinColor],
            patternSkin,
          ),
          resolveAssetElement(
            hairColorConfig[bookData.gender][bookData.hairColor][
              bookData.hairStyle
            ],
            patternHair,
          ),
        ],
      );

      if (bookData.glasses) {
        objects.push(...[...objects, patternGlasses]);
      }

      return objects;
    },

    pages: [
      {
        pageId: '3',
        pageImageURL: 'assets/templates_lets-make-metro/03007/03007.jpg',
        pageImagePDFURL: 'assets/templates_lets-make-metro/03007/03007.pdf',
        text: [jp.SEGMENT_3_text_1],
      },
    ],
  } as TSegment,
  {
    segmentId: '4',
    objectProcessor: (bookData) => {
      const objects: (ImageElement | TextElement)[] = [];
      const patternSkin: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL:
          'assets/templates_lets-make-metro/04008/M/04008-M{{assetSkin}}.png',
        imagePDFURL:
          'assets/templates_lets-make-metro/04008/M/04008-M{{assetSkin}}.pdf',
        depth: 5,
      };
      const patternHair: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL:
          'assets/templates_lets-make-metro/04008/H/04008-H{{assetHair}}.png',
        imagePDFURL:
          'assets/templates_lets-make-metro/04008/H/04008-H{{assetHair}}.pdf',
        depth: 7,
      };

      const patternGlasses: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL: 'assets/templates_lets-make-metro/04008/G/04008-G01.png',
        imagePDFURL: 'assets/templates_lets-make-metro/04008/G/04008-G01.pdf',
        depth: 6,
      };

      objects.push(
        ...[
          resolveAssetElement(
            skinColorConfig[bookData.gender][bookData.skinColor],
            patternSkin,
          ),
          resolveAssetElement(
            hairColorConfig[bookData.gender][bookData.hairColor][
              bookData.hairStyle
            ],
            patternHair,
          ),
        ],
      );

      if (bookData.glasses) {
        objects.push(...[...objects, patternGlasses]);
      }

      return objects;
    },
    pages: [
      {
        pageId: '4',
        pageImageURL: 'assets/templates_lets-make-metro/04008/04008.jpg',
        pageImagePDFURL: 'assets/templates_lets-make-metro/04008/04008.pdf',
      },
    ],
  } as TSegment,
  {
    segmentId: '5',
    textProcessor: (bookData, text) => textProcessor(bookData, text),
    pages: [
      {
        pageId: '5',
        pageImageURL: 'assets/templates_lets-make-metro/05009/05009.jpg',
        pageImagePDFURL: 'assets/templates_lets-make-metro/05009/05009.pdf',
        text: [jp.SEGMENT_5_text_1],
      },
    ],
  } as TSegment,
  {
    segmentId: '6',
    pages: [
      {
        pageId: '6',
        pageImageURL: 'assets/templates_lets-make-metro/06010/06010.jpg',
        pageImagePDFURL: 'assets/templates_lets-make-metro/06010/06010.pdf',
      },
    ],
  } as TSegment,
  {
    segmentId: '7',
    textProcessor: (bookData, text) => textProcessor(bookData, text),
    objectProcessor: (bookData) => {
      const objects: (ImageElement | TextElement)[] = [];
      const patternSkin: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL:
          'assets/templates_lets-make-metro/07011/M/07011-M{{assetSkin}}.png',
        imagePDFURL:
          'assets/templates_lets-make-metro/07011/M/07011-M{{assetSkin}}.pdf',
        depth: 5,
      };
      const patternHair: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL:
          'assets/templates_lets-make-metro/07011/H/07011-H{{assetHair}}.png',
        imagePDFURL:
          'assets/templates_lets-make-metro/07011/H/07011-H{{assetHair}}.pdf',
        depth: 7,
      };

      const patternGlasses: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL: 'assets/templates_lets-make-metro/07011/G/07011-G01.png',
        imagePDFURL: 'assets/templates_lets-make-metro/07011/G/07011-G01.pdf',
        depth: 6,
      };

      objects.push(
        ...[
          resolveAssetElement(
            skinColorConfig[bookData.gender][bookData.skinColor],
            patternSkin,
          ),
          resolveAssetElement(
            hairColorConfig[bookData.gender][bookData.hairColor][
              bookData.hairStyle
            ],
            patternHair,
          ),
        ],
      );

      if (bookData.glasses) {
        objects.push(...[...objects, patternGlasses]);
      }

      return objects;
    },
    pages: [
      {
        pageId: '7',
        pageImageURL: 'assets/templates_lets-make-metro/07011/07011.jpg',
        pageImagePDFURL: 'assets/templates_lets-make-metro/07011/07011.pdf',
        text: [jp.SEGMENT_7_text_1],
      },
    ],
  } as TSegment,
  {
    segmentId: '8',
    objectProcessor: (bookData) => {
      const objects: (ImageElement | TextElement)[] = [];
      const patternSkin: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL:
          'assets/templates_lets-make-metro/08012/M/08012-M{{assetSkin}}.png',
        imagePDFURL:
          'assets/templates_lets-make-metro/08012/M/08012-M{{assetSkin}}.pdf',
        depth: 5,
      };
      const patternHair: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL:
          'assets/templates_lets-make-metro/08012/H/08012-H{{assetHair}}.png',
        imagePDFURL:
          'assets/templates_lets-make-metro/08012/H/08012-H{{assetHair}}.pdf',
        depth: 7,
      };

      const patternGlasses: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL: 'assets/templates_lets-make-metro/08012/G/08012-G01.png',
        imagePDFURL: 'assets/templates_lets-make-metro/08012/G/08012-G01.pdf',
        depth: 6,
      };

      objects.push(
        ...[
          resolveAssetElement(
            skinColorConfig[bookData.gender][bookData.skinColor],
            patternSkin,
          ),
          resolveAssetElement(
            hairColorConfig[bookData.gender][bookData.hairColor][
              bookData.hairStyle
            ],
            patternHair,
          ),
        ],
      );

      if (bookData.glasses) {
        objects.push(...[...objects, patternGlasses]);
      }

      return objects;
    },
    pages: [
      {
        pageId: '8',
        pageImageURL: 'assets/templates_lets-make-metro/08012/08012.jpg',
        pageImagePDFURL: 'assets/templates_lets-make-metro/08012/08012.pdf',
      },
    ],
  } as TSegment,
  {
    segmentId: '9',
    pages: [
      {
        pageId: '9',
        pageImageURL: 'assets/templates_lets-make-metro/09013/09013.jpg',
        pageImagePDFURL: 'assets/templates_lets-make-metro/09013/09013.pdf',
      },
    ],
  } as TSegment,
  {
    segmentId: '10',
    textProcessor: (bookData, text) => textProcessor(bookData, text),
    objectProcessor: (bookData) => {
      const objects: (ImageElement | TextElement)[] = [];
      const patternSkin: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL:
          'assets/templates_lets-make-metro/10014/M/10014-M{{assetSkin}}.png',
        imagePDFURL:
          'assets/templates_lets-make-metro/10014/M/10014-M{{assetSkin}}.pdf',
        depth: 5,
      };
      const patternHair: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL:
          'assets/templates_lets-make-metro/10014/H/10014-H{{assetHair}}.png',
        imagePDFURL:
          'assets/templates_lets-make-metro/10014/H/10014-H{{assetHair}}.pdf',
        depth: 7,
      };

      const patternGlasses: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL: 'assets/templates_lets-make-metro/10014/G/10014-G01.png',
        imagePDFURL: 'assets/templates_lets-make-metro/10014/G/10014-G01.pdf',
        depth: 6,
      };

      objects.push(
        ...[
          resolveAssetElement(
            skinColorConfig[bookData.gender][bookData.skinColor],
            patternSkin,
          ),
          resolveAssetElement(
            hairColorConfig[bookData.gender][bookData.hairColor][
              bookData.hairStyle
            ],
            patternHair,
          ),
        ],
      );

      if (bookData.glasses) {
        objects.push(...[...objects, patternGlasses]);
      }

      return objects;
    },
    pages: [
      {
        pageId: '10',
        pageImageURL: 'assets/templates_lets-make-metro/10014/10014.jpg',
        pageImagePDFURL: 'assets/templates_lets-make-metro/10014/10014.pdf',
        text: [jp.SEGMENT_10_text_1],
      },
    ],
  } as TSegment,
  {
    segmentId: '11',
    pages: [
      {
        pageId: '11',
        pageImageURL: 'assets/templates_lets-make-metro/11015/11015.jpg',
        pageImagePDFURL: 'assets/templates_lets-make-metro/11015/11015.pdf',
      },
    ],
  } as TSegment,
  {
    segmentId: '12',
    textProcessor: (bookData, text) => textProcessor(bookData, text),
    pages: [
      {
        pageId: '12',
        pageImageURL: 'assets/templates_lets-make-metro/12016/12016.jpg',
        pageImagePDFURL: 'assets/templates_lets-make-metro/12016/12016.pdf',
        text: [jp.SEGMENT_12_text_1, jp.SEGMENT_12_text_2],
      },
    ],
  } as TSegment,
  {
    segmentId: '13',
    textProcessor: (bookData, text) => textProcessor(bookData, text),

    objectProcessor: (bookData) => {
      const objects: (ImageElement | TextElement)[] = [];

      if (bookData.storyLine === 'train_creation') {
        const patternSkin: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/13063/M/13063-M{{assetSkin}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/13063/M/13063-M{{assetSkin}}.pdf',
          depth: 5,
        };
        const patternHair: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/13063/H/13063-H{{assetHair}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/13063/H/13063-H{{assetHair}}.pdf',
          depth: 7,
        };
        const patternGlasses: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL: 'assets/templates_lets-make-metro/13063/G/13063-G01.png',
          imagePDFURL: 'assets/templates_lets-make-metro/13063/G/13063-G01.pdf',
          depth: 6,
        };
        objects.push(
          ...[
            resolveAssetElement(
              skinColorConfig[bookData.gender][bookData.skinColor],
              patternSkin,
            ),
            resolveAssetElement(
              hairColorConfig[bookData.gender][bookData.hairColor][
                bookData.hairStyle
              ],
              patternHair,
            ),
          ],
        );
        if (bookData.glasses) {
          objects.push(...[...objects, patternGlasses]);
        }
      }

      if (bookData.storyLine === 'station_creation') {
        const patternSkin: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/13064/M/13064-M{{assetSkin}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/13064/M/13064-M{{assetSkin}}.pdf',
          depth: 5,
        };
        const patternHair: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/13064/H/13064-H{{assetHair}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/13064/H/13064-H{{assetHair}}.pdf',
          depth: 7,
        };
        const patternGlasses: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL: 'assets/templates_lets-make-metro/13064/G/13064-G01.png',
          imagePDFURL: 'assets/templates_lets-make-metro/13064/G/13064-G01.pdf',
          depth: 6,
        };
        objects.push(
          ...[
            resolveAssetElement(
              skinColorConfig[bookData.gender][bookData.skinColor],
              patternSkin,
            ),
            resolveAssetElement(
              hairColorConfig[bookData.gender][bookData.hairColor][
                bookData.hairStyle
              ],
              patternHair,
            ),
          ],
        );
        if (bookData.glasses) {
          objects.push(...[...objects, patternGlasses]);
        }
      }
      if (bookData.storyLine === 'tunnel_creation') {
        const patternSkin: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/13065/M/13065-M{{assetSkin}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/13065/M/13065-M{{assetSkin}}.pdf',
          depth: 5,
        };
        const patternHair: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/13065/H/13065-H{{assetHair}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/13065/H/13065-H{{assetHair}}.pdf',
          depth: 7,
        };
        const patternGlasses: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL: 'assets/templates_lets-make-metro/13065/G/13065-G01.png',
          imagePDFURL: 'assets/templates_lets-make-metro/13065/G/13065-G01.pdf',
          depth: 6,
        };
        objects.push(
          ...[
            resolveAssetElement(
              skinColorConfig[bookData.gender][bookData.skinColor],
              patternSkin,
            ),
            resolveAssetElement(
              hairColorConfig[bookData.gender][bookData.hairColor][
                bookData.hairStyle
              ],
              patternHair,
            ),
          ],
        );
        if (bookData.glasses) {
          objects.push(...[...objects, patternGlasses]);
        }
      }
      if (bookData.storyLine === 'train_conductor') {
        const patternSkin: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/13066/M/13066-M{{assetSkin}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/13066/M/13066-M{{assetSkin}}.pdf',
          depth: 5,
        };
        const patternHair: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/13066/H/13066-H{{assetHair}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/13066/H/13066-H{{assetHair}}.pdf',
          depth: 7,
        };
        const patternGlasses: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL: 'assets/templates_lets-make-metro/13066/G/13066-G01.png',
          imagePDFURL: 'assets/templates_lets-make-metro/13066/G/13066-G01.pdf',
          depth: 6,
        };
        objects.push(
          ...[
            resolveAssetElement(
              skinColorConfig[bookData.gender][bookData.skinColor],
              patternSkin,
            ),
            resolveAssetElement(
              hairColorConfig[bookData.gender][bookData.hairColor][
                bookData.hairStyle
              ],
              patternHair,
            ),
          ],
        );
        if (bookData.glasses) {
          objects.push(...[...objects, patternGlasses]);
        }
      }
      return objects;
    },
    getPages: (bookData: IBookTMLetsMakeMetroData) =>
      bookData.storyLine ? segment13Pages[bookData.storyLine] : [],
    pages: [],
  } as TSegment,
  {
    segmentId: '14',
    textProcessor: (bookData, text) => textProcessor(bookData, text),

    objectProcessor: (bookData) => {
      const objects: (ImageElement | TextElement)[] = [];

      if (bookData.storyLine === 'tunnel_creation') {
        const patternSkin: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99032/M/99032-M{{assetSkin}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99032/M/99032-M{{assetSkin}}.pdf',
          depth: 5,
        };
        const patternHair: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99032/H/99032-H{{assetHair}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99032/H/99032-H{{assetHair}}.pdf',
          depth: 7,
        };

        const patternGlasses: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL: 'assets/templates_lets-make-metro/99032/G/99032-G01.png',
          imagePDFURL: 'assets/templates_lets-make-metro/99032/G/99032-G01.pdf',
          depth: 6,
        };

        objects.push(
          ...[
            resolveAssetElement(
              skinColorConfig[bookData.gender][bookData.skinColor],
              patternSkin,
            ),
            resolveAssetElement(
              hairColorConfig[bookData.gender][bookData.hairColor][
                bookData.hairStyle
              ],
              patternHair,
            ),
          ],
        );

        if (bookData.glasses) {
          objects.push(...[...objects, patternGlasses]);
        }
      }

      if (bookData.storyLine === 'station_creation') {
        const patternSkin: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99033/M/99033-M{{assetSkin}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99033/M/99033-M{{assetSkin}}.pdf',
          depth: 5,
        };
        const patternHair: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99033/H/99033-H{{assetHair}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99033/H/99033-H{{assetHair}}.pdf',
          depth: 7,
        };

        const patternGlasses: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL: 'assets/templates_lets-make-metro/99033/G/99033-G01.png',
          imagePDFURL: 'assets/templates_lets-make-metro/99033/G/99033-G01.pdf',
          depth: 6,
        };

        objects.push(
          ...[
            resolveAssetElement(
              skinColorConfig[bookData.gender][bookData.skinColor],
              patternSkin,
            ),
            resolveAssetElement(
              hairColorConfig[bookData.gender][bookData.hairColor][
                bookData.hairStyle
              ],
              patternHair,
            ),
          ],
        );

        if (bookData.glasses) {
          objects.push(...[...objects, patternGlasses]);
        }
      }

      if (bookData.storyLine === 'train_conductor') {
        const patternSkin: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99034/M/99034-M{{assetSkin}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99034/M/99034-M{{assetSkin}}.pdf',
          depth: 5,
        };
        const patternHair: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99034/H/99034-H{{assetHair}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99034/H/99034-H{{assetHair}}.pdf',
          depth: 7,
        };

        const patternGlasses: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL: 'assets/templates_lets-make-metro/99034/G/99034-G01.png',
          imagePDFURL: 'assets/templates_lets-make-metro/99034/G/99034-G01.pdf',
          depth: 6,
        };

        objects.push(
          ...[
            resolveAssetElement(
              skinColorConfig[bookData.gender][bookData.skinColor],
              patternSkin,
            ),
            resolveAssetElement(
              hairColorConfig[bookData.gender][bookData.hairColor][
                bookData.hairStyle
              ],
              patternHair,
            ),
          ],
        );

        if (bookData.glasses) {
          objects.push(...[...objects, patternGlasses]);
        }
      }

      return objects;
    },
    getPages: (bookData: IBookTMLetsMakeMetroData) =>
      bookData.storyLine ? segment14Pages[bookData.storyLine] : [],
    pages: [],
  } as TSegment,
  {
    segmentId: '15',
    textProcessor: (bookData, text) => textProcessor(bookData, text),

    objectProcessor: (bookData) => {
      const objects: (ImageElement | TextElement)[] = [];

      if (bookData.storyLine === 'train_creation') {
        const patternSkin: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99035/M/99035-M{{assetSkin}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99035/M/99035-M{{assetSkin}}.pdf',
          depth: 5,
        };
        const patternHair: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99035/H/99035-H{{assetHair}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99035/H/99035-H{{assetHair}}.pdf',
          depth: 7,
        };

        const patternGlasses: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL: 'assets/templates_lets-make-metro/99035/G/99035-G01.png',
          imagePDFURL: 'assets/templates_lets-make-metro/99035/G/99035-G01.pdf',
          depth: 6,
        };

        objects.push(
          ...[
            resolveAssetElement(
              skinColorConfig[bookData.gender][bookData.skinColor],
              patternSkin,
            ),
            resolveAssetElement(
              hairColorConfig[bookData.gender][bookData.hairColor][
                bookData.hairStyle
              ],
              patternHair,
            ),
          ],
        );

        if (bookData.glasses) {
          objects.push(...[...objects, patternGlasses]);
        }
      }
      return objects;
    },
    getPages: (bookData: IBookTMLetsMakeMetroData) =>
      bookData.storyLine ? segment15Pages[bookData.storyLine] : [],
    pages: [],
  } as TSegment,
  {
    segmentId: '16',
    textProcessor: (bookData, text) => textProcessor(bookData, text),

    objectProcessor: (bookData) => {
      const objects: (ImageElement | TextElement)[] = [];
      if (bookData.storyLine === 'train_creation') {
        const patternSkin: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99039/M/99039-M{{assetSkin}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99039/M/99039-M{{assetSkin}}.pdf',
          depth: 5,
        };
        const patternHair: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99039/H/99039-H{{assetHair}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99039/H/99039-H{{assetHair}}.pdf',
          depth: 7,
        };

        const patternGlasses: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL: 'assets/templates_lets-make-metro/99039/G/99039-G01.png',
          imagePDFURL: 'assets/templates_lets-make-metro/99039/G/99039-G01.pdf',
          depth: 6,
        };

        objects.push(
          ...[
            resolveAssetElement(
              skinColorConfig[bookData.gender][bookData.skinColor],
              patternSkin,
            ),
            resolveAssetElement(
              hairColorConfig[bookData.gender][bookData.hairColor][
                bookData.hairStyle
              ],
              patternHair,
            ),
          ],
        );

        if (bookData.glasses) {
          objects.push(...[...objects, patternGlasses]);
        }
      }
      if (bookData.storyLine === 'tunnel_creation') {
        const patternSkin: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99040/M/99040-M{{assetSkin}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99040/M/99040-M{{assetSkin}}.pdf',
          depth: 5,
        };
        const patternHair: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99040/H/99040-H{{assetHair}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99040/H/99040-H{{assetHair}}.pdf',
          depth: 7,
        };

        const patternGlasses: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL: 'assets/templates_lets-make-metro/99040/G/99040-G01.png',
          imagePDFURL: 'assets/templates_lets-make-metro/99040/G/99040-G01.pdf',
          depth: 6,
        };

        objects.push(
          ...[
            resolveAssetElement(
              skinColorConfig[bookData.gender][bookData.skinColor],
              patternSkin,
            ),
            resolveAssetElement(
              hairColorConfig[bookData.gender][bookData.hairColor][
                bookData.hairStyle
              ],
              patternHair,
            ),
          ],
        );

        if (bookData.glasses) {
          objects.push(...[...objects, patternGlasses]);
        }
      }

      if (bookData.storyLine === 'station_creation') {
        const patternSkin: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99041/M/99041-M{{assetSkin}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99041/M/99041-M{{assetSkin}}.pdf',
          depth: 5,
        };
        const patternHair: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99041/H/99041-H{{assetHair}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99041/H/99041-H{{assetHair}}.pdf',
          depth: 7,
        };

        const patternGlasses: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL: 'assets/templates_lets-make-metro/99041/G/99041-G01.png',
          imagePDFURL: 'assets/templates_lets-make-metro/99041/G/99041-G01.pdf',
          depth: 6,
        };

        objects.push(
          ...[
            resolveAssetElement(
              skinColorConfig[bookData.gender][bookData.skinColor],
              patternSkin,
            ),
            resolveAssetElement(
              hairColorConfig[bookData.gender][bookData.hairColor][
                bookData.hairStyle
              ],
              patternHair,
            ),
          ],
        );

        if (bookData.glasses) {
          objects.push(...[...objects, patternGlasses]);
        }
      }

      if (bookData.storyLine === 'train_conductor') {
        const patternSkin: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99042/M/99042-M{{assetSkin}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99042/M/99042-M{{assetSkin}}.pdf',
          depth: 5,
        };
        const patternHair: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99042/H/99042-H{{assetHair}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99042/H/99042-H{{assetHair}}.pdf',
          depth: 7,
        };

        const patternGlasses: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL: 'assets/templates_lets-make-metro/99042/G/99042-G01.png',
          imagePDFURL: 'assets/templates_lets-make-metro/99042/G/99042-G01.pdf',
          depth: 6,
        };

        objects.push(
          ...[
            resolveAssetElement(
              skinColorConfig[bookData.gender][bookData.skinColor],
              patternSkin,
            ),
            resolveAssetElement(
              hairColorConfig[bookData.gender][bookData.hairColor][
                bookData.hairStyle
              ],
              patternHair,
            ),
          ],
        );

        if (bookData.glasses) {
          objects.push(...[...objects, patternGlasses]);
        }
      }

      return objects;
    },
    getPages: (bookData: IBookTMLetsMakeMetroData) =>
      bookData.storyLine ? segment16Pages[bookData.storyLine] : [],
    pages: [],
  } as TSegment,
  {
    segmentId: '17',
    textProcessor: (bookData, text) => textProcessor(bookData, text),

    objectProcessor: (bookData) => {
      const objects: (ImageElement | TextElement)[] = [];
      if (bookData.storyLine === 'train_creation') {
        const patternSkin: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99043/M/99043-M{{assetSkin}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99043/M/99043-M{{assetSkin}}.pdf',
          depth: 5,
        };
        const patternHair: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99043/H/99043-H{{assetHair}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99043/H/99043-H{{assetHair}}.pdf',
          depth: 7,
        };

        const patternGlasses: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL: 'assets/templates_lets-make-metro/99043/G/99043-G01.png',
          imagePDFURL: 'assets/templates_lets-make-metro/99043/G/99043-G01.pdf',
          depth: 6,
        };

        objects.push(
          ...[
            resolveAssetElement(
              skinColorConfig[bookData.gender][bookData.skinColor],
              patternSkin,
            ),
            resolveAssetElement(
              hairColorConfig[bookData.gender][bookData.hairColor][
                bookData.hairStyle
              ],
              patternHair,
            ),
          ],
        );

        if (bookData.glasses) {
          objects.push(...[...objects, patternGlasses]);
        }
      }
      if (bookData.storyLine === 'tunnel_creation') {
        const patternSkin: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99044/M/99044-M{{assetSkin}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99044/M/99044-M{{assetSkin}}.pdf',
          depth: 5,
        };
        const patternHair: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99044/H/99044-H{{assetHair}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99044/H/99044-H{{assetHair}}.pdf',
          depth: 7,
        };

        const patternGlasses: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL: 'assets/templates_lets-make-metro/99044/G/99044-G01.png',
          imagePDFURL: 'assets/templates_lets-make-metro/99044/G/99044-G01.pdf',
          depth: 6,
        };

        objects.push(
          ...[
            resolveAssetElement(
              skinColorConfig[bookData.gender][bookData.skinColor],
              patternSkin,
            ),
            resolveAssetElement(
              hairColorConfig[bookData.gender][bookData.hairColor][
                bookData.hairStyle
              ],
              patternHair,
            ),
          ],
        );

        if (bookData.glasses) {
          objects.push(...[...objects, patternGlasses]);
        }
      }

      if (bookData.storyLine === 'station_creation') {
        const patternSkin: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99045/M/99045-M{{assetSkin}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99045/M/99045-M{{assetSkin}}.pdf',
          depth: 5,
        };
        const patternHair: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99045/H/99045-H{{assetHair}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99045/H/99045-H{{assetHair}}.pdf',
          depth: 7,
        };

        const patternGlasses: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL: 'assets/templates_lets-make-metro/99045/G/99045-G01.png',
          imagePDFURL: 'assets/templates_lets-make-metro/99045/G/99045-G01.pdf',
          depth: 6,
        };

        objects.push(
          ...[
            resolveAssetElement(
              skinColorConfig[bookData.gender][bookData.skinColor],
              patternSkin,
            ),
            resolveAssetElement(
              hairColorConfig[bookData.gender][bookData.hairColor][
                bookData.hairStyle
              ],
              patternHair,
            ),
          ],
        );

        if (bookData.glasses) {
          objects.push(...[...objects, patternGlasses]);
        }
      }

      return objects;
    },
    getPages: (bookData: IBookTMLetsMakeMetroData) =>
      bookData.storyLine ? segment17Pages[bookData.storyLine] : [],
    pages: [],
  } as TSegment,
  {
    segmentId: '18',
    textProcessor: (bookData, text) => textProcessor(bookData, text),

    objectProcessor: (bookData) => {
      const objects: (ImageElement | TextElement)[] = [];
      if (bookData.storyLine === 'train_creation') {
        const patternSkin: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99047/M/99047-M{{assetSkin}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99047/M/99047-M{{assetSkin}}.pdf',
          depth: 5,
        };
        const patternHair: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99047/H/99047-H{{assetHair}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99047/H/99047-H{{assetHair}}.pdf',
          depth: 7,
        };

        const patternGlasses: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL: 'assets/templates_lets-make-metro/99047/G/99047-G01.png',
          imagePDFURL: 'assets/templates_lets-make-metro/99047/G/99047-G01.pdf',
          depth: 6,
        };

        objects.push(
          ...[
            resolveAssetElement(
              skinColorConfig[bookData.gender][bookData.skinColor],
              patternSkin,
            ),
            resolveAssetElement(
              hairColorConfig[bookData.gender][bookData.hairColor][
                bookData.hairStyle
              ],
              patternHair,
            ),
          ],
        );

        if (bookData.glasses) {
          objects.push(...[...objects, patternGlasses]);
        }
      }
      if (bookData.storyLine === 'tunnel_creation') {
        const patternSkin: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99048/M/99048-M{{assetSkin}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99048/M/99048-M{{assetSkin}}.pdf',
          depth: 5,
        };
        const patternHair: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99048/H/99048-H{{assetHair}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99048/H/99048-H{{assetHair}}.pdf',
          depth: 7,
        };

        const patternGlasses: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL: 'assets/templates_lets-make-metro/99048/G/99048-G01.png',
          imagePDFURL: 'assets/templates_lets-make-metro/99048/G/99048-G01.pdf',
          depth: 6,
        };

        objects.push(
          ...[
            resolveAssetElement(
              skinColorConfig[bookData.gender][bookData.skinColor],
              patternSkin,
            ),
            resolveAssetElement(
              hairColorConfig[bookData.gender][bookData.hairColor][
                bookData.hairStyle
              ],
              patternHair,
            ),
          ],
        );

        if (bookData.glasses) {
          objects.push(...[...objects, patternGlasses]);
        }
      }

      if (bookData.storyLine === 'station_creation') {
        const patternSkin: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99049/M/99049-M{{assetSkin}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99049/M/99049-M{{assetSkin}}.pdf',
          depth: 5,
        };
        const patternHair: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99049/H/99049-H{{assetHair}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99049/H/99049-H{{assetHair}}.pdf',
          depth: 7,
        };

        const patternGlasses: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL: 'assets/templates_lets-make-metro/99049/G/99049-G01.png',
          imagePDFURL: 'assets/templates_lets-make-metro/99049/G/99049-G01.pdf',
          depth: 6,
        };

        objects.push(
          ...[
            resolveAssetElement(
              skinColorConfig[bookData.gender][bookData.skinColor],
              patternSkin,
            ),
            resolveAssetElement(
              hairColorConfig[bookData.gender][bookData.hairColor][
                bookData.hairStyle
              ],
              patternHair,
            ),
          ],
        );

        if (bookData.glasses) {
          objects.push(...[...objects, patternGlasses]);
        }
      }

      if (bookData.storyLine === 'train_conductor') {
        const patternSkin: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99050/M/99050-M{{assetSkin}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99050/M/99050-M{{assetSkin}}.pdf',
          depth: 5,
        };
        const patternHair: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99050/H/99050-H{{assetHair}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99050/H/99050-H{{assetHair}}.pdf',
          depth: 7,
        };

        const patternGlasses: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL: 'assets/templates_lets-make-metro/99050/G/99050-G01.png',
          imagePDFURL: 'assets/templates_lets-make-metro/99050/G/99050-G01.pdf',
          depth: 6,
        };

        objects.push(
          ...[
            resolveAssetElement(
              skinColorConfig[bookData.gender][bookData.skinColor],
              patternSkin,
            ),
            resolveAssetElement(
              hairColorConfig[bookData.gender][bookData.hairColor][
                bookData.hairStyle
              ],
              patternHair,
            ),
          ],
        );

        if (bookData.glasses) {
          objects.push(...[...objects, patternGlasses]);
        }
      }

      return objects;
    },
    getPages: (bookData: IBookTMLetsMakeMetroData) =>
      bookData.storyLine ? segment18Pages[bookData.storyLine] : [],
    pages: [],
  } as TSegment,
  {
    segmentId: '19',
    textProcessor: (bookData, text) => textProcessor(bookData, text),

    objectProcessor: (bookData) => {
      const objects: (ImageElement | TextElement)[] = [];

      if (bookData.storyLine === 'tunnel_creation') {
        const patternSkin: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99052/M/99052-M{{assetSkin}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99052/M/99052-M{{assetSkin}}.pdf',
          depth: 5,
        };
        const patternHair: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99052/H/99052-H{{assetHair}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99052/H/99052-H{{assetHair}}.pdf',
          depth: 7,
        };

        const patternGlasses: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL: 'assets/templates_lets-make-metro/99052/G/99052-G01.png',
          imagePDFURL: 'assets/templates_lets-make-metro/99052/G/99052-G01.pdf',
          depth: 6,
        };

        objects.push(
          ...[
            resolveAssetElement(
              skinColorConfig[bookData.gender][bookData.skinColor],
              patternSkin,
            ),
            resolveAssetElement(
              hairColorConfig[bookData.gender][bookData.hairColor][
                bookData.hairStyle
              ],
              patternHair,
            ),
          ],
        );

        if (bookData.glasses) {
          objects.push(...[...objects, patternGlasses]);
        }
      }

      if (bookData.storyLine === 'station_creation') {
        const patternSkin: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99053/M/99053-M{{assetSkin}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99053/M/99053-M{{assetSkin}}.pdf',
          depth: 5,
        };
        const patternHair: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99053/H/99053-H{{assetHair}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99053/H/99053-H{{assetHair}}.pdf',
          depth: 7,
        };

        const patternGlasses: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL: 'assets/templates_lets-make-metro/99053/G/99053-G01.png',
          imagePDFURL: 'assets/templates_lets-make-metro/99053/G/99053-G01.pdf',
          depth: 6,
        };

        objects.push(
          ...[
            resolveAssetElement(
              skinColorConfig[bookData.gender][bookData.skinColor],
              patternSkin,
            ),
            resolveAssetElement(
              hairColorConfig[bookData.gender][bookData.hairColor][
                bookData.hairStyle
              ],
              patternHair,
            ),
          ],
        );

        if (bookData.glasses) {
          objects.push(...[...objects, patternGlasses]);
        }
      }

      if (bookData.storyLine === 'train_conductor') {
        const patternSkin: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99054/M/99054-M{{assetSkin}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99054/M/99054-M{{assetSkin}}.pdf',
          depth: 5,
        };
        const patternHair: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99054/H/99054-H{{assetHair}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99054/H/99054-H{{assetHair}}.pdf',
          depth: 7,
        };

        const patternGlasses: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL: 'assets/templates_lets-make-metro/99054/G/99054-G01.png',
          imagePDFURL: 'assets/templates_lets-make-metro/99054/G/99054-G01.pdf',
          depth: 6,
        };

        objects.push(
          ...[
            resolveAssetElement(
              skinColorConfig[bookData.gender][bookData.skinColor],
              patternSkin,
            ),
            resolveAssetElement(
              hairColorConfig[bookData.gender][bookData.hairColor][
                bookData.hairStyle
              ],
              patternHair,
            ),
          ],
        );

        if (bookData.glasses) {
          objects.push(...[...objects, patternGlasses]);
        }
      }

      return objects;
    },
    getPages: (bookData: IBookTMLetsMakeMetroData) =>
      bookData.storyLine ? segment19Pages[bookData.storyLine] : [],
    pages: [],
  } as TSegment,
  {
    segmentId: '20',
    textProcessor: (bookData, text) => textProcessor(bookData, text),

    objectProcessor: (bookData) => {
      const objects: (ImageElement | TextElement)[] = [];
      if (bookData.storyLine === 'train_creation') {
        const patternSkin: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99055/M/99055-M{{assetSkin}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99055/M/99055-M{{assetSkin}}.pdf',
          depth: 5,
        };
        const patternHair: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99055/H/99055-H{{assetHair}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99055/H/99055-H{{assetHair}}.pdf',
          depth: 7,
        };

        const patternGlasses: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL: 'assets/templates_lets-make-metro/99055/G/99055-G01.png',
          imagePDFURL: 'assets/templates_lets-make-metro/99055/G/99055-G01.pdf',
          depth: 6,
        };

        objects.push(
          ...[
            resolveAssetElement(
              skinColorConfig[bookData.gender][bookData.skinColor],
              patternSkin,
            ),
            resolveAssetElement(
              hairColorConfig[bookData.gender][bookData.hairColor][
                bookData.hairStyle
              ],
              patternHair,
            ),
          ],
        );

        if (bookData.glasses) {
          objects.push(...[...objects, patternGlasses]);
        }
      }
      if (bookData.storyLine === 'tunnel_creation') {
        const patternSkin: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99056/M/99056-M{{assetSkin}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99056/M/99056-M{{assetSkin}}.pdf',
          depth: 5,
        };
        const patternHair: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99056/H/99056-H{{assetHair}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99056/H/99056-H{{assetHair}}.pdf',
          depth: 7,
        };

        const patternGlasses: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL: 'assets/templates_lets-make-metro/99056/G/99056-G01.png',
          imagePDFURL: 'assets/templates_lets-make-metro/99056/G/99056-G01.pdf',
          depth: 6,
        };

        objects.push(
          ...[
            resolveAssetElement(
              skinColorConfig[bookData.gender][bookData.skinColor],
              patternSkin,
            ),
            resolveAssetElement(
              hairColorConfig[bookData.gender][bookData.hairColor][
                bookData.hairStyle
              ],
              patternHair,
            ),
          ],
        );

        if (bookData.glasses) {
          objects.push(...[...objects, patternGlasses]);
        }
      }

      return objects;
    },
    getPages: (bookData: IBookTMLetsMakeMetroData) =>
      bookData.storyLine ? segment20Pages[bookData.storyLine] : [],
    pages: [],
  } as TSegment,
  {
    segmentId: '21',
    textProcessor: (bookData, text) => textProcessor(bookData, text),

    objectProcessor: (bookData) => {
      const objects: (ImageElement | TextElement)[] = [];

      if (bookData.storyLine === 'station_creation') {
        const patternSkin: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99061/M/99061-M{{assetSkin}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99061/M/99061-M{{assetSkin}}.pdf',
          depth: 5,
        };
        const patternHair: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99061/H/99061-H{{assetHair}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99061/H/99061-H{{assetHair}}.pdf',
          depth: 7,
        };

        const patternGlasses: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL: 'assets/templates_lets-make-metro/99061/G/99061-G01.png',
          imagePDFURL: 'assets/templates_lets-make-metro/99061/G/99061-G01.pdf',
          depth: 6,
        };

        objects.push(
          ...[
            resolveAssetElement(
              skinColorConfig[bookData.gender][bookData.skinColor],
              patternSkin,
            ),
            resolveAssetElement(
              hairColorConfig[bookData.gender][bookData.hairColor][
                bookData.hairStyle
              ],
              patternHair,
            ),
          ],
        );

        if (bookData.glasses) {
          objects.push(...[...objects, patternGlasses]);
        }
      }

      if (bookData.storyLine === 'train_conductor') {
        const patternSkin: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99062/M/99062-M{{assetSkin}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99062/M/99062-M{{assetSkin}}.pdf',
          depth: 5,
        };
        const patternHair: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL:
            'assets/templates_lets-make-metro/99062/H/99062-H{{assetHair}}.png',
          imagePDFURL:
            'assets/templates_lets-make-metro/99062/H/99062-H{{assetHair}}.pdf',
          depth: 7,
        };

        const patternGlasses: ImageElement = {
          type: 'ImageElement',
          x: -8,
          y: -8,
          imageURL: 'assets/templates_lets-make-metro/99062/G/99062-G01.png',
          imagePDFURL: 'assets/templates_lets-make-metro/99062/G/99062-G01.pdf',
          depth: 6,
        };

        objects.push(
          ...[
            resolveAssetElement(
              skinColorConfig[bookData.gender][bookData.skinColor],
              patternSkin,
            ),
            resolveAssetElement(
              hairColorConfig[bookData.gender][bookData.hairColor][
                bookData.hairStyle
              ],
              patternHair,
            ),
          ],
        );

        if (bookData.glasses) {
          objects.push(...[...objects, patternGlasses]);
        }
      }

      return objects;
    },
    getPages: (bookData: IBookTMLetsMakeMetroData) =>
      bookData.storyLine ? segment21Pages[bookData.storyLine] : [],
    pages: [],
  } as TSegment,
  {
    segmentId: '22',

    textProcessor: (bookData, text) => textProcessor(bookData, text),
    objectProcessor: (bookData) => {
      const objects: (ImageElement | TextElement)[] = [];
      const patternSkin: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL:
          'assets/templates_lets-make-metro/22018/M/22018-M{{assetSkin}}.png',
        imagePDFURL:
          'assets/templates_lets-make-metro/22018/M/22018-M{{assetSkin}}.pdf',
        depth: 5,
      };
      const patternHair: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL:
          'assets/templates_lets-make-metro/22018/H/22018-H{{assetHair}}.png',
        imagePDFURL:
          'assets/templates_lets-make-metro/22018/H/22018-H{{assetHair}}.pdf',
        depth: 7,
      };

      const patternGlasses: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL: 'assets/templates_lets-make-metro/22018/G/22018-G01.png',
        imagePDFURL: 'assets/templates_lets-make-metro/22018/G/22018-G01.pdf',
        depth: 6,
      };

      objects.push(
        ...[
          resolveAssetElement(
            skinColorConfig[bookData.gender][bookData.skinColor],
            patternSkin,
          ),
          resolveAssetElement(
            hairColorConfig[bookData.gender][bookData.hairColor][
              bookData.hairStyle
            ],
            patternHair,
          ),
        ],
      );

      if (bookData.glasses) {
        objects.push(...[...objects, patternGlasses]);
      }

      return objects;
    },
    pages: [
      {
        pageId: '22',
        pageImageURL: 'assets/templates_lets-make-metro/22018/22018.jpg',
        pageImagePDFURL: 'assets/templates_lets-make-metro/22018/22018.pdf',
        text: [jp.SEGMENT_22_text_1],
      },
    ],
  } as TSegment,
  {
    segmentId: '23',
    pages: [
      {
        pageId: '23',
        pageImageURL: 'assets/templates_lets-make-metro/23019/23019.jpg',
        pageImagePDFURL: 'assets/templates_lets-make-metro/23019/23019.pdf',
      },
    ],
  } as TSegment,
  {
    segmentId: '24',
    textProcessor: (bookData, text) => textProcessor(bookData, text),
    pages: [
      {
        pageId: '24',
        pageImageURL: 'assets/templates_lets-make-metro/24020/24020.jpg',
        pageImagePDFURL: 'assets/templates_lets-make-metro/24020/24020.pdf',
        text: [jp.SEGMENT_24_text_1],
      },
    ],
  } as TSegment,
  {
    segmentId: '25',
    textProcessor: (bookData, text) => textProcessor(bookData, text),

    objectProcessor: (bookData) => {
      const objects: (ImageElement | TextElement)[] = [];
      const patternSkin: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -6,
        imageURL:
          'assets/templates_lets-make-metro/25021/M/25021-M{{assetSkin}}.png',
        imagePDFURL:
          'assets/templates_lets-make-metro/25021/M/25021-M{{assetSkin}}.pdf',
        depth: 5,
      };
      const patternHair: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL:
          'assets/templates_lets-make-metro/25021/H/25021-H{{assetHair}}.png',
        imagePDFURL:
          'assets/templates_lets-make-metro/25021/H/25021-H{{assetHair}}.pdf',
        depth: 7,
      };

      const patternGlasses: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -6,
        imageURL: 'assets/templates_lets-make-metro/25021/G/25021-G01.png',
        imagePDFURL: 'assets/templates_lets-make-metro/25021/G/25021-G01.pdf',
        depth: 6,
      };

      objects.push(
        ...[
          resolveAssetElement(
            skinColorConfig[bookData.gender][bookData.skinColor],
            patternSkin,
          ),
          resolveAssetElement(
            hairColorConfig[bookData.gender][bookData.hairColor][
              bookData.hairStyle
            ],
            patternHair,
          ),
        ],
      );

      if (bookData.glasses) {
        objects.push(...[...objects, patternGlasses]);
      }

      return objects;
    },
    pages: [
      {
        pageId: '25',
        pageImageURL: 'assets/templates_lets-make-metro/25021/25021.jpg',
        pageImagePDFURL: 'assets/templates_lets-make-metro/25021/25021.pdf',
      },
    ],
  } as TSegment,
  {
    segmentId: '26',
    pages: [
      {
        pageId: '26',
        pageImageURL: 'assets/templates_lets-make-metro/26022/26022.jpg',
        pageImagePDFURL: 'assets/templates_lets-make-metro/26022/26022.pdf',
      },
    ],
  } as TSegment,
  {
    segmentId: '27',
    textProcessor: (bookData, text) => textProcessor(bookData, text),
    objectProcessor: (bookData) => {
      const objects: (ImageElement | TextElement)[] = [];
      const patternSkin: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL:
          'assets/templates_lets-make-metro/27023/M/27023-M{{assetSkin}}.png',
        imagePDFURL:
          'assets/templates_lets-make-metro/27023/M/27023-M{{assetSkin}}.pdf',
        depth: 5,
      };
      const patternHair: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL:
          'assets/templates_lets-make-metro/27023/H/27023-H{{assetHair}}.png',
        imagePDFURL:
          'assets/templates_lets-make-metro/27023/H/27023-H{{assetHair}}.pdf',
        depth: 7,
      };

      const patternGlasses: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL: 'assets/templates_lets-make-metro/27023/G/27023-G01.png',
        imagePDFURL: 'assets/templates_lets-make-metro/27023/G/27023-G01.pdf',
        depth: 6,
      };

      objects.push(
        ...[
          resolveAssetElement(
            skinColorConfig[bookData.gender][bookData.skinColor],
            patternSkin,
          ),
          resolveAssetElement(
            hairColorConfig[bookData.gender][bookData.hairColor][
              bookData.hairStyle
            ],
            patternHair,
          ),
        ],
      );

      if (bookData.glasses) {
        objects.push(...[...objects, patternGlasses]);
      }

      return objects;
    },
    pages: [
      {
        pageId: '27',
        pageImageURL: 'assets/templates_lets-make-metro/27023/27023.jpg',
        pageImagePDFURL: 'assets/templates_lets-make-metro/27023/27023.pdf',
        text: [jp.SEGMENT_27_text_1],
      },
    ],
  } as TSegment,
  {
    segmentId: '28',
    pages: [
      {
        pageId: '28',
        pageImageURL: 'assets/templates_lets-make-metro/28024/28024.jpg',
        pageImagePDFURL: 'assets/templates_lets-make-metro/28024/28024.pdf',
      },
    ],
  } as TSegment,
  {
    segmentId: '29',
    textProcessor: (bookData, text) => textProcessor(bookData, text),
    objectProcessor: (bookData) => {
      const objects: (ImageElement | TextElement)[] = [];
      const patternSkin: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL:
          'assets/templates_lets-make-metro/29025/M/29025-M{{assetSkin}}.png',
        imagePDFURL:
          'assets/templates_lets-make-metro/29025/M/29025-M{{assetSkin}}.pdf',
        depth: 5,
      };
      const patternHair: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL:
          'assets/templates_lets-make-metro/29025/H/29025-H{{assetHair}}.png',
        imagePDFURL:
          'assets/templates_lets-make-metro/29025/H/29025-H{{assetHair}}.pdf',
        depth: 7,
      };

      const patternGlasses: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL: 'assets/templates_lets-make-metro/29025/G/29025-G01.png',
        imagePDFURL: 'assets/templates_lets-make-metro/29025/G/29025-G01.pdf',
        depth: 6,
      };

      objects.push(
        ...[
          resolveAssetElement(
            skinColorConfig[bookData.gender][bookData.skinColor],
            patternSkin,
          ),
          resolveAssetElement(
            hairColorConfig[bookData.gender][bookData.hairColor][
              bookData.hairStyle
            ],
            patternHair,
          ),
        ],
      );

      if (bookData.glasses) {
        objects.push(...[...objects, patternGlasses]);
      }

      return objects;
    },
    pages: [
      {
        pageId: '29',
        pageImageURL: 'assets/templates_lets-make-metro/29025/29025.jpg',
        pageImagePDFURL: 'assets/templates_lets-make-metro/29025/29025.pdf',
        text: [jp.SEGMENT_29_text_1],
      },
    ],
  } as TSegment,
  {
    segmentId: '30',
    textProcessor: (bookData, text) => textProcessor(bookData, text),
    objectProcessor: (bookData) => {
      const objects: (ImageElement | TextElement)[] = [];
      const patternSkin: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL:
          'assets/templates_lets-make-metro/30026/M/30026-M{{assetSkin}}.png',
        imagePDFURL:
          'assets/templates_lets-make-metro/30026/M/30026-M{{assetSkin}}.pdf',
        depth: 5,
      };
      const patternHair: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL:
          'assets/templates_lets-make-metro/30026/H/30026-H{{assetHair}}.png',
        imagePDFURL:
          'assets/templates_lets-make-metro/30026/H/30026-H{{assetHair}}.pdf',
        depth: 7,
      };

      const patternGlasses: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL: 'assets/templates_lets-make-metro/30026/G/30026-G01.png',
        imagePDFURL: 'assets/templates_lets-make-metro/30026/G/30026-G01.pdf',
        depth: 6,
      };

      objects.push(
        ...[
          resolveAssetElement(
            skinColorConfig[bookData.gender][bookData.skinColor],
            patternSkin,
          ),
          resolveAssetElement(
            hairColorConfig[bookData.gender][bookData.hairColor][
              bookData.hairStyle
            ],
            patternHair,
          ),
        ],
      );

      if (bookData.glasses) {
        objects.push(...[...objects, patternGlasses]);
      }

      return objects;
    },
    pages: [
      {
        pageId: '30',
        pageImageURL: 'assets/templates_lets-make-metro/30026/30026.jpg',
        pageImagePDFURL: 'assets/templates_lets-make-metro/30026/30026.pdf',
        text: [jp.SEGMENT_30_text_1, jp.SEGMENT_30_text_2],
      },
    ],
  } as TSegment,
  {
    segmentId: '31',
    pages: [
      {
        pageId: '31',
        pageImageURL: 'assets/templates_lets-make-metro/31027/31027.jpg',
        pageImagePDFURL: 'assets/templates_lets-make-metro/31027/31027.pdf',
      },
    ],
  } as TSegment,
  {
    segmentId: '32',
    textProcessor: (bookData, text) => textProcessor(bookData, text),
    pages: [
      {
        pageId: '32',
        pageImageURL: 'assets/templates_lets-make-metro/32028/32028.jpg',
        pageImagePDFURL: 'assets/templates_lets-make-metro/32028/32028.pdf',
        text: [jp.SEGMENT_32_text_1],
      },
    ],
  } as TSegment,
  {
    segmentId: '33',
    textProcessor: (bookData, text) => textProcessor(bookData, text),

    getPages: (bookData: IBookTMLetsMakeMetroData) => {
      const pages: Segment<
        TSegmentTermsType,
        TPageTermsType,
        IBookTMLetsMakeMetroData
      >['pages'] = [];
      const textArr: TextElement[] = [];

      textArr.push(
        ...leftTextElement(
          bookData,
          jp.SEGMENT_33_text_value_1,
          bookData.cover === 'hard' ? (isNode ? 238 : 238) : isNode ? 238 : 238,
          {
            fontSize: 8,
            fontFamily: 'MPlusRounded1c',
            fill: '#ffffff',
          },
          {
            fontSize: 6,
            fontFamily: 'MPlusRounded1c',
            fill: '#ffffff',
            lineHeight: 1,
          },
          8,
          7,
          107,
        ),
      );

      pages.push({
        pageId: '0',
        pageImageURL: 'assets/templates_lets-make-metro/33029/33029.jpg',
        pageImagePDFURL: 'assets/templates_lets-make-metro/33029/33029.pdf',
        text: textArr,
      });
      return pages;
    },

    pages: [],
  } as TSegment,

  {
    segmentId: '34',
    textProcessor: (bookData, text) => textProcessor(bookData, text),
    objectProcessor: (bookData) => {
      const objects: (ImageElement | TextElement)[] = [];
      const patternSkin: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL:
          'assets/templates_lets-make-metro/34030/M/34030-M{{assetSkin}}.png',
        imagePDFURL:
          'assets/templates_lets-make-metro/34030/M/34030-M{{assetSkin}}.pdf',
        depth: 5,
      };
      const patternHair: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL:
          'assets/templates_lets-make-metro/34030/H/34030-H{{assetHair}}.png',
        imagePDFURL:
          'assets/templates_lets-make-metro/34030/H/34030-H{{assetHair}}.pdf',
        depth: 7,
      };

      const patternGlasses: ImageElement = {
        type: 'ImageElement',
        x: -8,
        y: -8,
        imageURL: 'assets/templates_lets-make-metro/34030/G/34030-G01.png',
        imagePDFURL: 'assets/templates_lets-make-metro/34030/G/34030-G01.pdf',
        depth: 6,
      };

      objects.push(
        ...[
          resolveAssetElement(
            skinColorConfig[bookData.gender][bookData.skinColor],
            patternSkin,
          ),
          resolveAssetElement(
            hairColorConfig[bookData.gender][bookData.hairColor][
              bookData.hairStyle
            ],
            patternHair,
          ),
        ],
      );

      if (bookData.glasses) {
        objects.push(...[...objects, patternGlasses]);
      }

      return objects;
    },

    getPages: (bookData: IBookTMLetsMakeMetroData) => {
      const pages: Segment<
        TSegmentTermsType,
        TPageTermsType,
        IBookTMLetsMakeMetroData
      >['pages'] = [];
      const textArr: TextElement[] = [];

      textArr.push(
        ...centerTextElement(
          bookData,
          jp.SEGMENT_34_text_value_1,
          500,
          {
            fontSize: 18,
            fontFamily: 'YDWTYRANNOGOCHI',
            fill: '#000000',
          },
          {
            fontSize: 12,
            fontFamily: 'YDWTYRANNOGOCHI',
            fill: '#000000',
            lineHeight: 1,
          },
          16,
          11,
        ),
      );

      pages.push({
        pageId: '0',
        pageImageURL: 'assets/templates_lets-make-metro/34030/34030.jpg',
        pageImagePDFURL: 'assets/templates_lets-make-metro/34030/34030.pdf',
        text: textArr,
        data: {
          messageRect: {
            x: 329,
            y: 139,
            xPdf: 329,
            yPdf: 139,
            width: 460,
            height: 264,
            style: {
              ...APJapanesefont_000000_18,
            },
          },
        },
      });
      return pages;
    },

    pages: [],
  } as TSegment,

  {
    segmentId: '35',
    objectProcessor: (bookData) => {
      const objects: (ImageElement | TextElement)[] = [];
      const patternSkin: ImageElement = {
        type: 'ImageElement',
        x: -6,
        y: -15,
        imageURL:
          'assets/templates_lets-make-metro/00001/M/00001-M{{assetSkin}}.png',
        imagePDFURL:
          'assets/templates_lets-make-metro/00001/M/00001-M{{assetSkin}}.pdf',
        depth: 5,
      };
      const patternHair: ImageElement = {
        type: 'ImageElement',
        x: -6,
        y: -15,
        imageURL:
          'assets/templates_lets-make-metro/00001/H/00001-H{{assetHair}}.png',
        imagePDFURL:
          'assets/templates_lets-make-metro/00001/H/00001-H{{assetHair}}.pdf',
        depth: 7,
      };

      const patternGlasses: ImageElement = {
        type: 'ImageElement',
        x: -6,
        y: -15,
        imageURL: 'assets/templates_lets-make-metro/00001/G/00001-G01.png',
        imagePDFURL: 'assets/templates_lets-make-metro/00001/G/00001-G01.pdf',
        depth: 6,
      };

      objects.push(
        ...[
          resolveAssetElement(
            skinColorConfig[bookData.gender][bookData.skinColor],
            patternSkin,
          ),
          resolveAssetElement(
            hairColorConfig[bookData.gender][bookData.hairColor][
              bookData.hairStyle
            ],
            patternHair,
          ),
        ],
      );

      if (bookData.glasses) {
        objects.push(...[...objects, patternGlasses]);
      }

      return objects;
    },
    pages: [
      {
        pageId: '35',
        pageImageURL: 'assets/templates_lets-make-metro/00001/00001.jpg',
        pageImagePDFURL: 'assets/templates_lets-make-metro/00001/00001.pdf',
      },
    ],
  } as TSegment,
] as const;

export default segments;
export const SEGMENTS_LIST_FONTS_MAP = segmentsListToFontsMap<
  typeof segments,
  TLanguage
>(segments, languageArr as unknown as TLanguage[], {
  [INPUT_VALUE_JAPANESE]: new Set([
    'KozGoProRegular',
    'AFSSerifGothic04trialE',
    'CorporateLogoRound',
    'APJapanesefont',
  ]),
  [INPUT_VALUE_ENGLISH]: new Set([]),
});
export * from './_utils';
